import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { motion, AnimatePresence } from 'framer-motion';
import './Devices.css';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:4310';

const Devices = () => {
  const DEFAULT_WINDOWS_DOWNLOAD_URL = 'https://www.dropbox.com/scl/fi/afllqekpp0lwo5a0i23x5/CrackIt-Setup-1.0.0.exe?rlkey=eu2zq43483gnw1ony4nululgw&st=mse3kmm7&dl=1';
  const DEFAULT_MAC_DOWNLOAD_URL = 'https://www.dropbox.com/scl/fi/su8lex4z8wkb0zjqnjbwf/CrackIt-1.0.0-arm64.dmg?rlkey=595ep1wi1dhdvn1jqel01qoa4&st=w2n09ffr&dl=1';

  const [windowsDownloadUrl, setWindowsDownloadUrl] = useState(DEFAULT_WINDOWS_DOWNLOAD_URL);
  const [macDownloadUrl, setMacDownloadUrl] = useState(DEFAULT_MAC_DOWNLOAD_URL);

  useEffect(() => {
    fetch(`${API_URL}/api/downloads`)
      .then(res => res.json())
      .then(data => {
        if (data && data.success && data.downloads) {
          if (data.downloads.windows) setWindowsDownloadUrl(data.downloads.windows);
          if (data.downloads.mac) setMacDownloadUrl(data.downloads.mac);
        }
      })
      .catch(err => console.log('[Devices] Using default download URLs:', err));
  }, []);

  const [activeModal, setActiveModal] = useState(null); // 'mac' | 'windows' | null
  const [macInstallTab, setMacInstallTab] = useState('curl'); // 'curl' | 'rightclick'
  const [copiedCurl, setCopiedCurl] = useState(false);
  const [copiedXattr, setCopiedXattr] = useState(false);

  const CURL_COMMAND = 'curl -fsSL https://crackitdude.com/install-mac.sh | bash';
  const XATTR_COMMAND = 'xattr -cr /Applications/CrackIt.app';

  const handleCopy = (text, type) => {
    navigator.clipboard.writeText(text);
    if (type === 'curl') {
      setCopiedCurl(true);
      setTimeout(() => setCopiedCurl(false), 2500);
    } else if (type === 'xattr') {
      setCopiedXattr(true);
      setTimeout(() => setCopiedXattr(false), 2500);
    }
  };

  const devices = [
    {
      key: 'windows',
      name: 'Windows',
      icon: (
        <svg width="28" height="28" viewBox="0 0 24 24" fill="currentColor">
          <path d="M0 3.449L9.75 2.1v9.451H0m10.949-9.602L24 0v11.4H10.949M0 12.6h9.75v9.451L0 20.699M10.949 12.6H24V24l-12.901-1.799"/>
        </svg>
      ),
      description: 'Windows 10 / 11 (64-bit)',
      spec: 'DirectML Accelerated',
      downloadUrl: windowsDownloadUrl,
      buttonText: 'Download Windows',
      fileType: '.EXE Installer',
      available: true
    },
    {
      key: 'mac',
      name: 'macOS',
      icon: (
        <svg width="28" height="28" viewBox="0 0 24 24" fill="currentColor">
          <path d="M18.71 19.5c-.83 1.24-1.71 2.45-3.05 2.47-1.34.03-1.77-.79-3.29-.79-1.53 0-2 .77-3.27.82-1.31.05-2.3-1.32-3.14-2.53C4.25 17 2.94 12.45 4.7 9.39c.87-1.52 2.43-2.48 4.12-2.51 1.28-.02 2.5.87 3.29.87.78 0 2.26-1.07 3.81-.91.65.03 2.47.26 3.64 1.98-.09.06-2.17 1.28-2.15 3.81.03 3.02 2.65 4.03 2.68 4.04-.03.07-.42 1.44-1.38 2.83M15.97 6.38c.62-.75 1.04-1.8 0.92-2.88-.9.04-1.99.6-2.63 1.35-.57.65-1.07 1.72-.94 2.76 1 .08 2.03-.49 2.65-1.23"/>
        </svg>
      ),
      description: 'Apple Silicon & Intel',
      spec: 'Metal Accelerated',
      downloadUrl: macDownloadUrl,
      buttonText: 'Download macOS',
      fileType: '.DMG Universal',
      available: true
    },
    {
      key: 'linux',
      name: 'Linux',
      icon: (
        <svg width="28" height="28" viewBox="0 0 24 24" fill="currentColor">
          <circle cx="12" cy="12" r="10" fill="none" stroke="currentColor" strokeWidth="2"/>
          <path d="M8 12h8m-4-4v8"/>
        </svg>
      ),
      description: 'Ubuntu / Debian / Fedora',
      spec: 'Vulkan Accelerated',
      downloadUrl: null,
      buttonText: 'Coming Soon',
      fileType: '.AppImage',
      available: false
    }
  ];

  return (
    <section className="coprep-devices-section" id="download">
      <div className="coprep-devices-container">
        
        {/* Header */}
        <motion.div 
          className="section-header-centered"
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7 }}
          viewport={{ once: true }}
        >
          <div className="badge-pill">DESKTOP CLIENT</div>
          <h2 className="section-main-title">
            Desktop <span className="gradient-primary">Client</span>
          </h2>
          <p className="section-main-subtitle">
            Stealth desktop app with hardware-level overlay for macOS and Windows.
          </p>
        </motion.div>

        {/* Devices Cards */}
        <div className="devices-cards-grid">
          {devices.map((device, idx) => (
            <motion.div 
              key={device.key}
              className={`device-card glass-card ${device.available ? 'device-active' : 'device-disabled'}`}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: idx * 0.15 }}
              viewport={{ once: true }}
            >
              <div className="device-top-row">
                <div className="device-icon-box">
                  {device.icon}
                </div>
                <span className="device-spec-tag">{device.spec}</span>
              </div>

              <h3 className="device-name-heading">{device.name}</h3>
              <p className="device-os-desc">{device.description}</p>
              <span className="device-file-type">{device.fileType}</span>

              <div className="device-cta-wrapper">
                {device.available ? (
                  <button 
                    className="device-dl-btn btn-primary"
                    onClick={() => setActiveModal(device.key)}
                  >
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="7 10 12 15 17 10"/><line x1="12" x2="12" y1="15" y2="3"/></svg>
                    <span>{device.buttonText}</span>
                  </button>
                ) : (
                  <button className="device-dl-btn btn-disabled" disabled>
                    Coming Soon
                  </button>
                )}
              </div>
            </motion.div>
          ))}
        </div>

      </div>

      {/* INSTALLATION MODALS */}
      {typeof document !== 'undefined' && createPortal(
        <AnimatePresence>
          {activeModal === 'mac' && (
            <div className="install-modal-backdrop" onClick={() => setActiveModal(null)}>
              <motion.div 
                className="install-modal-card glass-card"
                initial={{ opacity: 0, scale: 0.95, y: 20 }}
                animate={{ opacity: 1, scale: 1, y: 0 }}
                exit={{ opacity: 0, scale: 0.95, y: 20 }}
                onClick={(e) => e.stopPropagation()}
              >
                <div className="modal-top-bar">
                  <div className="modal-title-group">
                    <div className="modal-icon-badge">
                      <svg width="22" height="22" viewBox="0 0 24 24" fill="currentColor">
                        <path d="M18.71 19.5c-.83 1.24-1.71 2.45-3.05 2.47-1.34.03-1.77-.79-3.29-.79-1.53 0-2 .77-3.27.82-1.31.05-2.3-1.32-3.14-2.53C4.25 17 2.94 12.45 4.7 9.39c.87-1.52 2.43-2.48 4.12-2.51 1.28-.02 2.5.87 3.29.87.78 0 2.26-1.07 3.81-.91.65.03 2.47.26 3.64 1.98-.09.06-2.17 1.28-2.15 3.81.03 3.02 2.65 4.03 2.68 4.04-.03.07-.42 1.44-1.38 2.83M15.97 6.38c.62-.75 1.04-1.8 0.92-2.88-.9.04-1.99.6-2.63 1.35-.57.65-1.07 1.72-.94 2.76 1 .08 2.03-.49 2.65-1.23"/>
                      </svg>
                    </div>
                    <h3 className="modal-title">macOS Installation Instructions</h3>
                  </div>
                  <button className="install-modal-close-btn" onClick={() => setActiveModal(null)} aria-label="Close dialog">
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                      <line x1="18" y1="6" x2="6" y2="18"></line>
                      <line x1="6" y1="6" x2="18" y2="18"></line>
                    </svg>
                  </button>
                </div>

                <div className="modal-tabs-switch">
                  <button 
                    className={`modal-tab-btn ${macInstallTab === 'curl' ? 'active' : ''}`}
                    onClick={() => setMacInstallTab('curl')}
                  >
                    Option 1: 1-Click Terminal Setup (Recommended)
                  </button>
                  <button 
                    className={`modal-tab-btn ${macInstallTab === 'rightclick' ? 'active' : ''}`}
                    onClick={() => setMacInstallTab('rightclick')}
                  >
                    Option 2: Direct .DMG Download
                  </button>
                </div>

                {macInstallTab === 'curl' ? (
                  <div className="modal-tab-body">
                    <p className="modal-guide-text">
                      Open Terminal (press <kbd>Cmd</kbd>+<kbd>Space</kbd>, type <strong>Terminal</strong>, and hit Enter), then paste:
                    </p>
                    <div className="terminal-copy-box">
                      <code>{CURL_COMMAND}</code>
                      <button className="copy-code-btn" onClick={() => handleCopy(CURL_COMMAND, 'curl')}>
                        {copiedCurl ? 'Copied!' : 'Copy'}
                      </button>
                    </div>
                    <p className="modal-sub-note" style={{ display: 'inline-flex', alignItems: 'center', gap: '6px' }}>
                      <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><path d="M13 2L3 14h9l-1 8 10-12h-9l1-8z"/></svg>
                      Automatically downloads, verifies, and launches CrackIT Copilot without Gatekeeper warnings.
                    </p>
                  </div>
                ) : (
                  <div className="modal-tab-body">
                    <p className="modal-guide-text">
                      Download the universal macOS .DMG installer:
                    </p>
                    <a 
                      href={macDownloadUrl} 
                      className="btn-primary modal-action-download"
                      target="_blank" 
                      rel="noopener noreferrer"
                    >
                      <span>Download CrackIt-1.0.0-arm64.dmg</span>
                    </a>
                    <div className="gatekeeper-warning-box">
                      <span className="gatekeeper-title">If macOS displays "Unidentified Developer":</span>
                      <p className="gatekeeper-desc">Run this command in Terminal to clear quarantine attributes:</p>
                      <div className="terminal-copy-box">
                        <code>{XATTR_COMMAND}</code>
                        <button className="copy-code-btn" onClick={() => handleCopy(XATTR_COMMAND, 'xattr')}>
                          {copiedXattr ? 'Copied!' : 'Copy'}
                        </button>
                      </div>
                    </div>
                  </div>
                )}
              </motion.div>
            </div>
          )}

          {activeModal === 'windows' && (
            <div className="install-modal-backdrop" onClick={() => setActiveModal(null)}>
              <motion.div 
                className="install-modal-card glass-card"
                initial={{ opacity: 0, scale: 0.95, y: 20 }}
                animate={{ opacity: 1, scale: 1, y: 0 }}
                exit={{ opacity: 0, scale: 0.95, y: 20 }}
                onClick={(e) => e.stopPropagation()}
              >
                <div className="modal-top-bar">
                  <div className="modal-title-group">
                    <div className="modal-icon-badge">
                      <svg width="22" height="22" viewBox="0 0 24 24" fill="currentColor">
                        <path d="M0 3.449L9.75 2.1v9.451H0m10.949-9.602L24 0v11.4H10.949M0 12.6h9.75v9.451L0 20.699M10.949 12.6H24V24l-12.901-1.799"/>
                      </svg>
                    </div>
                    <h3 className="modal-title">Windows Installation Guide</h3>
                  </div>
                  <button className="install-modal-close-btn" onClick={() => setActiveModal(null)} aria-label="Close dialog">
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                      <line x1="18" y1="6" x2="6" y2="18"></line>
                      <line x1="6" y1="6" x2="18" y2="18"></line>
                    </svg>
                  </button>
                </div>

                <div className="modal-tab-body">
                  <p className="modal-guide-text">
                    Download and run the native 64-bit Windows setup executable:
                  </p>
                  <a 
                    href={windowsDownloadUrl} 
                    className="btn-primary modal-action-download"
                    target="_blank" 
                    rel="noopener noreferrer"
                  >
                    <span>Download CrackIt-Setup-1.0.0.exe</span>
                  </a>
                  <div className="gatekeeper-warning-box">
                    <span className="gatekeeper-title">If Windows Defender displays "SmartScreen Protected Your PC":</span>
                    <p className="gatekeeper-desc">Click <strong>"More info"</strong> and then click <strong>"Run anyway"</strong>.</p>
                  </div>
                </div>
              </motion.div>
            </div>
          )}
        </AnimatePresence>,
        document.body
      )}

    </section>
  );
};

export default Devices;
